import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'

import { runReportedMutation } from '@/store/mutationHelpers'
import { CreateTeacherParameters } from '@/util/Teacher'
import { normalizeEmail, normalizeUsername } from '@/util/util'

import CreateTeacher from '../mutations/CreateTeacher.graphql'

export interface AdminState {
  error: string
  updating: boolean
}

const resetState = (): AdminState => {
  return {
    error: '',
    updating: false,
  }
}

const admin = defineModule({
  actions: {
    /** Create a teacher. Resolves false on failure, with the reason in `error` for CreateTeacher to show inline. */
    async createTeacher(context, payload: CreateTeacherParameters): Promise<boolean> {
      // eslint-disable-next-line no-use-before-define
      const { commit } = adminActionContext(context)

      commit.setUpdating(true)
      try {
        const result = await runReportedMutation(
          CreateTeacher,
          {
            userData: {
              email: normalizeEmail(payload.email),
              firstName: payload.firstName,
              lastName: payload.lastName,
              username: normalizeUsername(payload.username),
            },
          },
          'createTeacher',
          commit.setError,
        )
        if (result) {
          commit.setError('')
        }
        return !!result
      } finally {
        commit.setUpdating(false)
      }
    },

    reset(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = adminActionContext(context)
      commit.reset()
    },

    resetError(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = adminActionContext(context)
      commit.setError('')
    },
  },
  getters: {
    error(...args): string {
      // eslint-disable-next-line no-use-before-define
      const { state } = adminGetterContext(args)
      return state.error
    },

    updating(...args): boolean {
      // eslint-disable-next-line no-use-before-define
      const { state } = adminGetterContext(args)
      return state.updating
    },
  },
  mutations: {
    reset(state: AdminState) {
      Object.assign(state, { ...resetState() })
    },

    setError(state: AdminState, error: string) {
      state.error = error
    },

    setUpdating(state: AdminState, updating: boolean) {
      state.updating = updating
    },
  },

  namespaced: true as const,

  state: resetState(),
})

export default admin
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const adminGetterContext = (args: [any, any, any, any]) => localGetterContext(args, admin)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const adminActionContext = (context: any) => localActionContext(context, admin)
